/**
 * ehr#910: a note's text box keeps what was typed while the note catches up.
 *
 * Fast typing dropped letters ("kept herawake", "Denies uicidal"). Every keystroke re-drew the
 * whole note (slow: a big page), so keystrokes queued up behind it; with any other update waiting
 * (a server answer, a timer), 50 queued keystrokes in a row made React stop one ("Maximum update
 * depth exceeded") and the box went back to the text before it: that letter was gone.
 *
 * Now the box (components/NoteTextarea) holds the typed text itself and tells the note in the
 * background. The note's copy of the text can then be a few keystrokes behind for a moment, so when
 * a value comes back from the note the box decides what it is:
 * - the same text as the box: in step (nothing waiting any more);
 * - an earlier text the box sent: the note catching up, the box keeps its newer text;
 * - anything else: a change from outside (a phrase, dictation, Adjust, Keep or Revert, a reload),
 *   which the box shows.
 */
export function syncBox(
  box: string,
  sent: readonly string[],
  incoming: string,
): { box: string; sent: string[] } {
  if (incoming === box) return { box, sent: [] };
  const i = sent.indexOf(incoming);
  if (i >= 0) return { box, sent: sent.slice(i + 1) };
  return { box: incoming, sent: [] };
}

/** Keys that act on the note right away (save, sign, next box, submit): the note gets the box's text first. */
export const flushesFirst = (e: {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
}): boolean =>
  e.ctrlKey || e.metaKey || e.altKey || e.key === 'Enter' || e.key === 'Tab' || e.key === 'Escape';
